<template>
  <v-dialog :value="value" max-width="1160" scrollable @input="$emit('input', $event)">
    <v-card class="kf-offers-dialog">
      <v-card-title class="kf-offers-dialog__head">
        <div>
          <div class="kf-offers-dialog__title">{{ customerName }}</div>
          <!-- Says what was clicked to get here, so the popup can be read on
               its own without remembering which row opened it. -->
          <div class="kf-offers-dialog__sub">
            {{ fmt.mobile(customerMobile) }}
            <template v-if="customer">
              &nbsp;·&nbsp;{{ customer.clicked_count }} of {{ customer.offer_count }} clicked
            </template>
            <!-- A field dropped from the table for being constant is stated
                 here instead. Dropping the column must not drop the fact. -->
            <template v-for="c in constants">
              &nbsp;·&nbsp;<span :key="c">{{ c }}</span>
            </template>
          </div>
        </div>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <LucideIcon name="x" :size="20" />
        </v-btn>
      </v-card-title>

      <v-card-text class="kf-offers-dialog__body">
        <!-- DataTable owns the loading skeleton, the empty state and the error
             state, so this dialog gets all three without rebuilding them -- and
             the table matches every other table in the panel. total-pages 1
             keeps its pager hidden: this list is never paged. -->
        <DataTable
          class="kf-offers-dialog__table"
          :headers="headers"
          :items="offers"
          :loading="loading"
          :error="error"
          :total-pages="1"
          :total-count="offers.length"
          empty-title="No offers"
          empty-message="This customer has not been offered anything yet."
          empty-icon="tag-off"
          @retry="load"
        >
          <template #[`item.title`]="{ item }">
            <div class="kf-offer">
              <img
                v-if="item.logo"
                class="kf-offer__logo"
                :src="item.logo"
                :alt="item.title"
              />
              <span v-else class="kf-offer__logo kf-offer__logo--none">
                <LucideIcon name="tag" :size="14" color="#54636F" />
              </span>
              <div class="kf-stack">
                <div class="kf-truncate" :title="item.title">{{ item.title }}</div>
                <div
                  v-if="item.sub_title"
                  class="kf-stack__sub kf-truncate"
                  :title="item.sub_title"
                >
                  {{ item.sub_title }}
                </div>
              </div>
            </div>
          </template>

          <template #[`item.loan_id`]="{ item }">
            <span class="kf-muted">#{{ item.loan_id }}</span>
          </template>

          <template #[`item.lender`]="{ item }">
            <span v-if="item.lender">{{ item.lender }}</span>
            <span v-else class="kf-muted">-</span>
          </template>

          <template #[`item.clicked`]="{ item }">
            <span v-if="item.clicked" class="kf-yes">
              <LucideIcon name="circle-check" :size="13" color="#00796B" />
              Clicked
            </span>
            <span v-else class="kf-muted">Not clicked</span>
            <div v-if="item.repeat_of">
              <span
                class="kf-tag kf-tag--repeat"
                :title="`Applied again for the same product within an hour of ${item.repeat_of}`"
              >Repeat of {{ item.repeat_of }}</span>
            </div>
          </template>

          <template #[`item.stage`]="{ item }">
            <div class="kf-stack">
              <div><StatusChip :status="item.stage" small /></div>
              <div v-if="item.sub_status" class="kf-stack__sub kf-truncate">
                {{ item.sub_status }}
              </div>
            </div>
          </template>

          <template #[`item.disbursal_amount`]="{ item }">
            <div v-if="item.disbursal_amount" class="kf-stack kf-stack--end">
              <div class="kf-amount">{{ fmt.currency(item.disbursal_amount) }}</div>
              <div v-if="item.disbursal_date" class="kf-stack__sub">
                {{ fmt.date(item.disbursal_date) }}
              </div>
            </div>
            <span v-else class="kf-muted">-</span>
          </template>

          <template #[`item.reject_reason`]="{ item }">
            <span
              v-if="item.reject_reason"
              class="kf-truncate"
              :title="item.reject_reason"
            >
              {{ item.reject_reason }}
            </span>
            <span v-else class="kf-muted">-</span>
          </template>
        </DataTable>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Every offer one customer has, opened from the "N / N clicked" figure on User
 * Management.
 *
 * The row count here has to equal the figure that opened it, which is why the
 * endpoint behind this does no filtering of its own -- see CustomerOffersView.
 */
import DataTable from "./DataTable.vue";
import StatusChip from "./StatusChip.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";

export default {
  name: "KfCustomerOffersDialog",
  components: { DataTable, StatusChip },
  props: {
    value: { type: Boolean, default: false },
    /** The row this was opened from; null while nothing is open. */
    customer: { type: Object, default: null },
  },
  data() {
    return {
      fmt,
      loading: false,
      error: null,
      offers: [],
      loanCount: 0,
    };
  },
  computed: {
    customerName() {
      return (this.customer && this.customer.name) || "Offers";
    },
    customerMobile() {
      return (this.customer && this.customer.mobile) || "";
    },
    /** The values the table dropped a column for, restated in the header. */
    constants() {
      if (!this.offers.length) return [];
      return ["lender", "product_type"]
        .filter((field) => this.distinct(field) === 1)
        .map((field) => this.offers[0][field])
        .filter(Boolean);
    },
    /** How many different values a field takes across these offers. */
    distinct() {
      return (field) => new Set(this.offers.map((o) => o[field])).size;
    },
    /**
     * The columns, minus the ones that would repeat a single value.
     *
     * Loan, Lender and Type are constant for most customers -- one loan, one
     * lender, all personal loans -- and three columns of the same word push
     * Status, Disbursed and Reason off the right edge of the dialog. They
     * appear only when they actually distinguish one row from another, which
     * is the only thing a column is for. What they said is not lost: a dropped
     * field is stated once in the header instead.
     */
    headers() {
      const varies = (field, header) =>
        this.distinct(field) > 1 ? [header] : [];
      return [
        { text: "Offer", value: "title", width: 200 },
        ...(this.loanCount > 1
          ? [{ text: "Loan", value: "loan_id", width: 70 }]
          : []),
        ...varies("lender", { text: "Lender", value: "lender", width: 120 }),
        ...varies("product_type", { text: "Type", value: "product_type", width: 110 }),
        { text: "Clicked", value: "clicked", width: 100 },
        { text: "Status", value: "stage", width: 140 },
        { text: "Disbursed", value: "disbursal_amount", width: 115, align: "end" },
        { text: "Reason", value: "reject_reason", width: 150 },
      ];
    },
  },
  watch: {
    /* Fetch on open, not on mount: this component lives inside the table's row
       loop conceptually but is mounted once, and the customer changes. */
    value(isOpen) {
      if (isOpen) this.load();
    },
  },
  methods: {
    load() {
      if (!this.customer) return;
      this.loading = true;
      this.error = null;
      this.offers = [];
      api
        .getCustomerOffers(this.customer.user_id || this.customer.id)
        .then((data) => {
          this.offers = data.offers;
          this.loanCount = data.loan_count;
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.kf-offers-dialog__head {
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px 10px;
}
.kf-offers-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--kf-brand);
  line-height: 1.3;
}
.kf-offers-dialog__sub {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
/* The dialog supplies the padding; DataTable brings its own surface. */
.kf-offers-dialog__body {
  padding: 0 12px 14px;
}
.kf-offer {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.kf-offer__logo {
  width: 34px;
  height: 22px;
  object-fit: contain;
  flex: 0 0 auto;
}
.kf-offer__logo--none {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* The shared cell helpers. They are defined per screen rather than in one
   stylesheet -- see users/index.vue and applications/index.vue -- so this
   dialog carries its own copy to look like the tables it was opened from. */
.kf-stack {
  min-width: 0;
  padding: 2px 0;
}
.kf-stack__sub {
  font-size: var(--kf-fs-label);
  color: #000;
  /* Clip rather than wrap: a sub-line that wraps makes its row twice the
     height of its neighbours. The full value stays on the title attribute. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kf-stack--end {
  text-align: right;
}
.kf-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kf-tag {
  display: inline-block;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: var(--kf-fs-label);
}
/* The borrower applied for the same product again within the hour: a real
   second offer, counted everywhere, tagged so nobody calls them twice
   (leads.repeats on the server). */
.kf-tag--repeat {
  color: #1d4ed8;
  background: #dbeafe;
  border: 1px solid #93c5fd;
}
.kf-yes {
  color: #00897b;
  font-weight: 600;
  font-size: var(--kf-fs-meta);
  white-space: nowrap;
}
.kf-muted {
  color: #000;
}
.kf-amount {
  font-weight: 700;
  color: #000;
  white-space: nowrap;
}
</style>
